import { CreateNextmv } from '../../../libs/GraphQL';

// One supervisor action → one createNextmvRun call.
// Common input keys (dropOffDate, fulfillmentCenter, vehicles, previousRunId)
// are passed via `context` so callers don't have to repeat them.

export const RUN_TYPE = {
  RESEQUENCE: 'ResequenceReroute',
  REMOVE_STOP: 'RemoveStop',
  DRIVER_CALL_OFF: 'DriverCallOff',
  MISSED_STOPS_REATTEMPT: 'MissedStopsReattempt',
};

function withContext(context, body) {
  return {
    dropOffDate: context.dropOffDate,
    fulfillmentCenter: context.fulfillmentCenter,
    vehicles: context.vehicles || [],
    previousRunId: context.previousRunId || null,
    ...body,
  };
}

// ResequenceReroute covers both reorder and window-only edits — the schema
// already carries startTimeWindow + endTimeWindow on each ResequenceStopInput.
export function resequenceReroute({ context, resequenceStops }) {
  return CreateNextmv(
    withContext(context, {
      runType: RUN_TYPE.RESEQUENCE,
      resequenceStops,
    }),
  );
}

export function removeStop({ context, removedStops }) {
  return CreateNextmv(
    withContext(context, {
      runType: RUN_TYPE.REMOVE_STOP,
      removedStops,
    }),
  );
}

// DriverCallOff takes only previousRunId + missingVehicles. Window overrides
// are NOT part of this strategy — they belong in a follow-up ResequenceReroute
// (one anchored stop = one pin).
export function driverCallOff({ context, missingVehicles }) {
  return CreateNextmv(
    withContext(context, {
      runType: RUN_TYPE.DRIVER_CALL_OFF,
      missingVehicles,
    }),
  );
}

// MissedStopsReattempt folds previous-day missed stops into a later day's plan.
// The FE sends only the vehicle roster, the carried-over missed stops (no route
// lock), and previousRunId — the backend derives current-day stop locking from
// previousRunId. See docs/supervisor-dashboard-route-operations-tab.md §3
// for the run contracts.
export function missedStopsReattempt({ context, missedStops }) {
  return CreateNextmv(
    withContext(context, {
      runType: RUN_TYPE.MISSED_STOPS_REATTEMPT,
      missedStops,
    }),
  );
}

// Overrides the shift/target times on the vehicle matching `override.vehicleId`
// inside the staged vehicles array. Returns the array unchanged when there is
// no override. Throws when an override IS present but matches no staged
// vehicle — otherwise the supervisor's shift edit would silently drop and a
// meaningless run would fire and "confirm" a change that never applied. Used
// by the Update Route flow to push a supervisor's shift edit to NextMV without
// a dedicated endpoint.
export function applyVehicleShiftOverride(vehicles, override) {
  if (!override) return vehicles;
  let matched = false;
  const next = vehicles.map((v) => {
    if (v.id !== override.vehicleId) return v;
    matched = true;
    return {
      ...v,
      shiftStartTime: override.shiftStartTime,
      shiftEndTime: override.shiftEndTime,
      targetShiftEndTime: override.targetShiftEndTime,
    };
  });
  if (!matched) {
    throw new Error(
      `Shift override targets vehicle "${override.vehicleId}", which is not assigned to this route.`,
    );
  }
  return next;
}

// Pull statusCode / nextmvRunId / message out of the mutation response.
// Returns { ok, runId, message } so callers can branch without knowing
// the AppSync envelope shape.
export function unpackResponse(response) {
  const payload = response?.data?.createNextmvRun || {};
  return {
    ok: payload.statusCode === 200 || payload.statusCode === '200',
    runId: payload.nextmvRunId || '',
    message: payload.message || '',
  };
}
